// frontend/src/utils/tssUtils.js
// tssUtils.js (top-level)
if (typeof window !== "undefined") {
  window.__TSSUTILS_LOADED__ = "DEBUG_BUILD_1";
}
// ----------------------------------------------------

// TEST BUILD: TrainingPeaks-like rTSS (pace-based)
// - Firma en consola para confirmar que este archivo se usa.
// - Logs detallados para ver inputs/outputs.

// - Lee window.__TP_THRESHOLD_PACE__ para probar sin backend.
  
// ----------------------------------------------------

/* eslint-disable no-console */
const TSS_UTILS_VERSION = "TP_TEST_BUILD_2026-01_v3";

// Firma: si no ves esto en consola, NO estás usando este archivo.
if (typeof window !== "undefined") {
  console.log(`[tssUtils] LOADED ${TSS_UTILS_VERSION}`);
}

const isEnglish = (language) => String(language || "").toLowerCase().startsWith("en");

export const interpretTSS = (tssValue, language = "es") => {
  const en = isEnglish(language);
  if (tssValue < 50) return en ? "Recovery or very light training. Ideal for active rest days." : "Entrenamiento de recuperación o muy ligero. Ideal para días de descanso activo.";
  if (tssValue < 100) return en ? "Sustainable effort, suitable for base sessions." : "Esfuerzo sostenible y adecuado para sesiones de base.";
  if (tssValue < 150) return en ? "Moderate to intense training." : "Entrenamiento moderado a intenso.";
  if (tssValue < 200) return en ? "Intense training with significant effort." : "Entrenamiento intenso, esfuerzo significativo.";
  if (tssValue >= 200) return en ? "Extremely intense training." : "Entrenamiento extremadamente intenso.";
  return en ? "TSS value not recognized. Check the input data." : "Valor de TSS no reconocido. Verifica los datos de entrada.";
};

// ----------------- helpers -----------------

const DEBUG =
  (typeof window !== "undefined" && window.__TSS_DEBUG__ === true) ||
  (typeof process !== "undefined" && process?.env?.REACT_APP_TSS_DEBUG === "1");

const log = (...args) => {
  if (DEBUG) console.log("[tssUtils]", ...args);
};

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// si es enorme, probablemente ms
const normalizeDurationSeconds = (raw) => {
  const n = toNumber(raw, 0);
  if (!n || n <= 0) return 0;
  if (n > 1_000_000) return n / 1000;
  return n;
};

// "MM:SS" o "HH:MM:SS" -> segundos
const paceStringToSec = (pace) => {
  if (!pace || typeof pace !== "string") return 0;
  const parts = pace.trim().split(":").map((p) => parseInt(p, 10));
  if (!parts.length || parts.some((x) => !Number.isFinite(x))) return 0;

  if (parts.length === 2) {
    const [mm, ss] = parts;
    return mm * 60 + ss;
  }
  if (parts.length === 3) {
    const [hh, mm, ss] = parts;
    return hh * 3600 + mm * 60 + ss;
  }
  return 0;
};

const normalizePaceToSec = (pace) => {
  if (pace === null || pace === undefined) return 0;
  if (typeof pace === "number") return toNumber(pace, 0);
  if (typeof pace === "string") return paceStringToSec(pace);
  return 0;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const calculateIF = (num, den) => {
  const n = toNumber(num, NaN);
  const d = toNumber(den, NaN);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return NaN;
  return n / d;
};

const calculateTSS = (hours, IF) => {
  const h = toNumber(hours, NaN);
  const i = toNumber(IF, NaN);
  if (!Number.isFinite(h) || !Number.isFinite(i) || h <= 0) return NaN;
  const tss = h * i * i * 100;
  return Number.isFinite(tss) ? tss : NaN;
};

// Lee VP threshold pace desde:
// 0) window.__TP_THRESHOLD_PACE__ (para probar rápido)
// A) zones_target array: [{type:'VP', threshold:'07:30'}]
// B) zones_target object: { threshold_pace: "07:30" }
// C) user/profile: { runningThresholdPace: "07:30" }
const extractThresholdPaceSec = (data) => {
  // 0) override para tests (no tocar backend)
  if (typeof window !== "undefined") {
    const raw = window.__TP_THRESHOLD_PACE__;
    const sec = normalizePaceToSec(raw);
    if (sec > 0) {
      log("VP_FROM_WINDOW", { raw, sec });
      return sec;
    } else if (raw) {
      log("VP_FROM_WINDOW_INVALID", { raw, sec });
    }
  }

  const z = data?.zones_target;

  // A) array
  if (Array.isArray(z)) {
    const vp = z.find((x) => (x?.type || "").toUpperCase() === "VP");
    const thr = vp?.threshold ?? vp?.umbral ?? vp?.value;
    const sec = normalizePaceToSec(thr);
    if (sec > 0) log("VP_FROM_ZONES_ARRAY", { thr, sec });
    return sec;
  }

  // B) object
  if (z && typeof z === "object") {
    const thr =
      z.threshold_pace ||
      z.thresholdPace ||
      z.running_threshold_pace ||
      z.vp_threshold ||
      z.thresholdPaceSec;

    const sec = normalizePaceToSec(thr);
    if (sec > 0) log("VP_FROM_ZONES_OBJECT", { thr, sec });
    if (sec) return sec;
  }

  // C) profile/user
  const up =
    data?.user?.runningThresholdPace ??
    data?.user?.profile?.runningThresholdPace ??
    data?.profile?.runningThresholdPace ??
    data?.user?.runningThresholdPaceSec ??
    data?.profile?.threshold_pace;

  const sec = normalizePaceToSec(up);
  if (sec > 0) log("VP_FROM_PROFILE", { up, sec });
  return sec;
};

// FTP desde zones_target
const extractFTP = (data) => {
  const z = data?.zones_target;
  if (Array.isArray(z)) {
    const pw = z.find((x) => (x?.type || "").toUpperCase() === "POWER");
    return toNumber(pw?.threshold ?? pw?.value ?? 0, 0);
  }
  if (z && typeof z === "object") {
    return toNumber(z.functional_threshold_power ?? z.ftp ?? z.threshold_power ?? 0, 0);
  }
  return 0;
};

// HRmax desde zones_target o user/profile o sesión
const extractHRmax = (data, sessions) => {
  const z = data?.zones_target;
  const zMax = z && typeof z === "object" ? toNumber(z.max_heart_rate ?? z.maxHeartRate ?? 0, 0) : 0;
  const userMax = toNumber(data?.user?.hrMax ?? data?.profile?.hr_max ?? data?.profile?.hrMax ?? 0, 0);
  const sessionMax = toNumber(sessions?.maxHeartRate ?? sessions?.max_heart_rate ?? 0, 0);
  return zMax || userMax || sessionMax || 0;
};

// Clasificación de deporte
const sportFlags = (sportRaw) => {
  const s = (sportRaw || "").toString().toLowerCase();
  return {
    isRunning: s.includes("run") || s.includes("running") || s.includes("trail"),
    isCycling: s.includes("bike") || s.includes("cycling"),
    isStrength:
      s.includes("strength") ||
      s.includes("gym") ||
      s.includes("fitness") ||
      s.includes("workout") ||
      s.includes("training") ||
      s.includes("cardio_training") ||
      s.includes("fit") ||
      s.includes("hiit"),
    raw: s,
  };
};

													   

/**
 * @param {object} data activityData
 * @param {object} options
 * @param {boolean} options.allowRunningHrFallback - SOLO para pruebas. Por defecto false (modo TP rTSS)
 */
export const calculateTSSWithTerrain = (data, options = {}) => {
  const { allowRunningHrFallback = false, language = "es" } = options;
  const en = isEnglish(language);

  // Log inicial (para ver estructura)
  log("INPUT_KEYS", {
    hasActivity: !!data?.activity,
    hasSessions: !!data?.sessions,
    sport: data?.sessions?.sport ?? data?.activity?.sport,
    totalTimerTime: data?.activity?.totalTimerTime,
    avgPace: data?.sessions?.avgPace,
    gapPace: data?.sessions?.gapPace,
    ngpPace: data?.sessions?.ngpPace,
  });

  const durationSec = normalizeDurationSeconds(data?.activity?.totalTimerTime);
  if (!durationSec) {
    log("BAD_DURATION", { raw: data?.activity?.totalTimerTime, durationSec });
    return { tss: NaN, message: en ? "Invalid duration." : "Duración inválida." };
  }

  const hours = durationSec / 3600;

  const sessions = data?.sessions ?? {};
  const sportRaw = sessions?.sport ?? data?.activity?.sport ?? "";
  const { isRunning, isCycling, isStrength, raw: sport } = sportFlags(sportRaw);

  // ---------- RUNNING (TP rTSS) ----------
  if (isRunning) {
    const avgPaceRaw = sessions?.avgPace ?? sessions?.ritmo_medio ?? null;
    const gapPaceRaw =
      sessions?.gapPace ??
      sessions?.ngpPace ??
      sessions?.gap ??
      sessions?.ngp ??
      null;

    const avgPaceSec = normalizePaceToSec(avgPaceRaw);
    const gapPaceSec = normalizePaceToSec(gapPaceRaw);
    const usedPaceSec = gapPaceSec > 0 ? gapPaceSec : avgPaceSec;

    const thresholdPaceSec = extractThresholdPaceSec(data);

    log("RUN_INPUTS", {
      durationSec,
      hours,
      avgPaceRaw,
      gapPaceRaw,
      avgPaceSec,
      gapPaceSec,
      usedPaceSec,
      thresholdPaceSec,
      allowRunningHrFallback,
    });

    // ✅ Caso TP válido
    if (thresholdPaceSec > 0 && usedPaceSec > 0) {
      const IF = calculateIF(thresholdPaceSec, usedPaceSec);
      const tssVal = calculateTSS(hours, IF);

      log("RUN_PACE_RESULT", { IF, tssVal });

      if (!Number.isFinite(tssVal)) return { tss: NaN, message: en ? "Invalid TSS." : "TSS inválido." };
      return { tss: Number(tssVal.toFixed(2)), message: interpretTSS(tssVal, language) };
    }

    // Mensajes bien explícitos para debugging
    if (usedPaceSec <= 0 && thresholdPaceSec > 0) {
      return {
        tss: NaN,
        message: en
          ? "Not available: missing Pace (avgPace/gapPace). Check sessions.avgPace or sessions.gapPace/ngpPace."
          : "No disponible: falta Pace (avgPace/gapPace). Revisa sessions.avgPace o sessions.gapPace/ngpPace.",
      };
    }
    if (thresholdPaceSec <= 0 && usedPaceSec > 0) {
      return {
        tss: NaN,
        message: en
          ? "Not available: missing Threshold Pace (VP). To test: window.__TP_THRESHOLD_PACE__='07:30' and reload."
          : "No disponible: falta Threshold Pace (VP). Para probar: window.__TP_THRESHOLD_PACE__='07:30' y recargar.",
      };
    }

    // ❌ Por defecto (TP estricto): sin VP → no hay rTSS
    if (!allowRunningHrFallback) {
						
					
			  
				   
				   
					
						 
																   
		 

      return {
        tss: NaN,
        message: en
          ? "Not available: missing Threshold Pace (VP) and/or Pace. Enable window.__TSS_DEBUG__=true for details."
          : "No disponible: falta Threshold Pace (VP) y/o Pace. Activa window.__TSS_DEBUG__=true para ver detalles.",
      };
    }

    // (Solo pruebas) fallback por HR
    const avgHR = toNumber(sessions?.avgHeartRate ?? sessions?.avg_heart_rate ?? 0, 0);
    const hrMax = extractHRmax(data, sessions);

    if (avgHR > 0 && hrMax > 0) {
      let IF = calculateIF(avgHR, hrMax);
      IF = clamp(IF, 0, 0.9);
      const tssVal = calculateTSS(hours, IF);

      log("RUN_HR_FALLBACK_TEST_ONLY", { avgHR, hrMax, IF, tssVal });

      if (!Number.isFinite(tssVal)) return { tss: NaN, message: en ? "Invalid TSS." : "TSS inválido." };
      return { tss: Number(tssVal.toFixed(2)), message: interpretTSS(tssVal, language) };
    }

    return { tss: NaN, message: en ? "Insufficient data for TSS (running)." : "Datos insuficientes para TSS (running)." };
  }

  // ---------- CYCLING (potencia) ----------
  const normalizedPower = toNumber(data?.activity?.normalizedPower, 0);
  const ftp = extractFTP(data);

  if (isCycling && normalizedPower > 0 && ftp > 0) {
    let IF = calculateIF(normalizedPower, ftp);
    IF = clamp(IF, 0, 1.2);
    const tssVal = calculateTSS(hours, IF);

    log("BIKE_POWER", { durationSec, hours, normalizedPower, ftp, IF, tssVal });

    if (!Number.isFinite(tssVal)) return { tss: NaN, message: en ? "Invalid TSS." : "TSS inválido." };
    return { tss: Number(tssVal.toFixed(2)), message: interpretTSS(tssVal, language) };
  }

  // ---------- STRENGTH / GYM ----------
  if (isStrength) {
    const avgHR = toNumber(sessions?.avgHeartRate ?? sessions?.avg_heart_rate ?? 0, 0);
    const hrMax = extractHRmax(data, sessions);

    if (avgHR > 0 && hrMax > 0) {
      let IF = calculateIF(avgHR, hrMax);
      IF = clamp(IF, 0, 0.85);

      const STRENGTH_FACTOR = 0.4;
      const base = calculateTSS(hours, IF);
      const tssVal = Number.isFinite(base) ? base * STRENGTH_FACTOR : NaN;

      log("STRENGTH_HR", { durationSec, hours, avgHR, hrMax, IF, base, STRENGTH_FACTOR, tssVal });

      if (!Number.isFinite(tssVal)) return { tss: NaN, message: en ? "Invalid TSS." : "TSS inválido." };
      return { tss: Number(tssVal.toFixed(2)), message: interpretTSS(tssVal, language) };
    }

    return { tss: NaN, message: en ? "Insufficient data for TSS (strength)." : "Datos insuficientes para TSS (strength)." };
  }

  // ---------- FALLBACK GENERAL (HRmax) ----------
  const avgHR = toNumber(sessions?.avgHeartRate ?? sessions?.avg_heart_rate ?? 0, 0);
  const hrMax = extractHRmax(data, sessions);

  if (avgHR > 0 && hrMax > 0) {
    let IF = calculateIF(avgHR, hrMax);
    IF = clamp(IF, 0, 0.9);
    const tssVal = calculateTSS(hours, IF);

    log("HRMAX_FALLBACK", { durationSec, hours, sport, avgHR, hrMax, IF, tssVal });

    if (!Number.isFinite(tssVal)) return { tss: NaN, message: en ? "Invalid TSS." : "TSS inválido." };
    return { tss: Number(tssVal.toFixed(2)), message: interpretTSS(tssVal, language) };
  }

  return { tss: NaN, message: en ? "Insufficient data to calculate TSS." : "Datos insuficientes para calcular TSS." };
};
export const __TSS_UTILS_VERSION__ = "TP_WINDOW_OVERRIDE_ENABLED_v1";
